import React, { useState, useEffect, useContext } from 'react';
import { Box, Card, CardContent, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Chip, Avatar, Button, IconButton, Dialog, DialogTitle, DialogContent, DialogActions, TextField, MenuItem, Select, FormControl, InputLabel, CircularProgress, Alert, InputAdornment, Tooltip, NavigateBefore, NavigateNext } from '@mui/material';
import { Add, Search, Edit, Delete, Phone } from '@mui/icons-material';
import { NavigateBefore as PrevIcon, NavigateNext as NextIcon } from '@mui/icons-material';
import axios from 'axios';
import { AuthContext } from '../context/AuthContext';
import { toLocalInputValue } from '../utils/dateUtils';
const API = '/api';
const PS = 15;
const SC = {pending:'#FF9800',completed:'#4CAF50',missed:'#F44336',rescheduled:'#2196F3'};
const PC = {low:'#9E9E9E',medium:'#FF9800',high:'#F44336'};
const EF = {lead:'',assignedTo:'',scheduledAt:'',reason:'',priority:'medium',notes:''};
 
export default function RecallQueue() {
  const {user} = useContext(AuthContext);
  const [recalls,setRecalls] = useState([]);
  const [leads,setLeads] = useState([]);
  const [users,setUsers] = useState([]);
  const [loading,setLoading] = useState(true);
  const [error,setError] = useState('');
  const [page,setPage] = useState(1);
  const [search,setSearch] = useState('');
  const [sf,setSf] = useState('');
  const [open,setOpen] = useState(false);
  const [editR,setEditR] = useState(null);
  const [form,setForm] = useState(EF);
  const [saving,setSaving] = useState(false);
  const [fe,setFe] = useState('');
 
  const hdr = {headers:{Authorization:'Bearer '+localStorage.getItem('token')}};
 
  const load = async () => {
    try { setLoading(true);
      const [rr,lr,ur] = await Promise.all([
        axios.get(API+'/recalls',hdr),
        axios.get(API+'/leads?limit=2000&page=1',hdr),
        user?.role==='admin'?axios.get(API+'/users',hdr):Promise.resolve({data:{users:[]}}),
      ]);
      setRecalls(rr.data.recalls||[]);
      setLeads(lr.data.leads||[]);
      setUsers(ur.data.users||[]);
    } catch(e) { setError(e.response?.data?.message||'Failed'); }
    finally { setLoading(false); }
  };
  useEffect(()=>{load();},[]);
 
  const filtered = recalls.filter(r=>{
    const q=search.toLowerCase();
    return (!q||((r.lead?.name || ((r.lead?.firstName||'') + ' ' + (r.lead?.lastName||'')).trim() || r.lead?.phone || 'Unknown') || ((r.lead?.firstName||'') + ' ' + (r.lead?.lastName||'')).trim() || r.lead?.phone || 'Unknown')?.toLowerCase().includes(q)||r.lead?.phone?.includes(q))&&(!sf||r.status===sf);
  });
  const tp=Math.ceil(filtered.length/PS)||1;
  const rows=filtered.slice((page-1)*PS,page*PS);
 
  const openAdd = () => { setEditR(null); setForm({...EF,assignedTo:user._id,scheduledAt:toLocalInputValue(new Date())}); setFe(''); setOpen(true); };
  const openEdit = r => { setEditR(r); setForm({lead:r.lead?._id||'',assignedTo:r.assignedTo?._id||'',scheduledAt:r.scheduledAt?toLocalInputValue(r.scheduledAt):'',reason:r.reason||'',priority:r.priority||'medium',notes:r.notes||'',status:r.status||'pending'}); setFe(''); setOpen(true); };
 
  const save = async () => {
    if(!form.lead||!form.scheduledAt) return setFe('Lead and scheduled time required');
    try { setSaving(true); setFe('');
      if(editR) await axios.put(API+'/recalls/'+editR._id,form,hdr);
      else await axios.post(API+'/recalls',form,hdr);
      setOpen(false); load();
    } catch(e) { setFe(e.response?.data?.message||'Failed'); }
    finally { setSaving(false); }
  };
 
  const del = async id => {
    if(!window.confirm('Delete recall?')) return;
    try { await axios.delete(API+'/recalls/'+id,hdr); load(); } catch(e) { alert('Failed'); }
  };
 
  const markDone = async r => {
    try { await axios.put(API+'/recalls/'+r._id,{status:'completed'},hdr); load(); } catch(e) { alert('Failed'); }
  };
 
  if(loading) return <Box display="flex" justifyContent="center" mt={8}><CircularProgress sx={{color:'#E8622A'}}/></Box>;
 
  return (
<Box p={3}>
<Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
<Box><Typography variant="h5" fontWeight={700}>Recall Queue</Typography>
<Typography variant="body2" color="text.secondary">{filtered.length} recalls · Page {page} of {tp}</Typography>
</Box>
<Button variant="contained" startIcon={<Add/>} onClick={openAdd} sx={{bgcolor:'#E8622A','&:hover':{bgcolor:'#C4501F'},borderRadius:2}}>Add Recall</Button>
</Box>
      {error&&<Alert severity="error" sx={{mb:2}}>{error}</Alert>}
<Card sx={{mb:2}}><CardContent sx={{py:1.5,'&:last-child':{pb:1.5}}}>
<Box display="flex" gap={2} flexWrap="wrap" alignItems="center">
<TextField size="small" placeholder="Search lead name or phone..." value={search} onChange={e=>{setSearch(e.target.value);setPage(1);}}
            InputProps={{startAdornment:<InputAdornment position="start"><Search fontSize="small"/></InputAdornment>}} sx={{minWidth:260}}/>
<FormControl size="small" sx={{minWidth:140}}><InputLabel>Status</InputLabel>
<Select value={sf} label="Status" onChange={e=>{setSf(e.target.value);setPage(1);}}>
<MenuItem value="">All</MenuItem>
              {['pending','completed','missed','rescheduled'].map(s=><MenuItem key={s} value={s}>{s.charAt(0).toUpperCase()+s.slice(1)}</MenuItem>)}
</Select>
</FormControl>
          {(search||sf)&&<Button size="small" onClick={()=>{setSearch('');setSf('');setPage(1);}}>Clear</Button>}
<Box ml="auto"><Chip label={recalls.filter(r=>r.status==='pending').length+' pending'} size="small" sx={{bgcolor:'#FF980020',color:'#FF9800',fontWeight:600}}/></Box>
</Box>
</CardContent></Card>
 
      <TableContainer component={Paper} elevation={1} sx={{borderRadius:2}}>
<Table size="small">
<TableHead><TableRow sx={{'& th':{bgcolor:'#F5F7FA',fontWeight:600,fontSize:12,color:'#555',py:1.5}}}>
<TableCell>#</TableCell><TableCell>Lead</TableCell><TableCell>Phone</TableCell>
<TableCell>Scheduled</TableCell><TableCell>Reason</TableCell><TableCell>Priority</TableCell>
<TableCell>Status</TableCell><TableCell>Actions</TableCell>
</TableRow></TableHead>
<TableBody>
            {rows.length===0?<TableRow><TableCell colSpan={8} align="center" sx={{py:5,color:'text.secondary'}}>No recalls found</TableCell></TableRow>
            :rows.map((r,i)=>(
<TableRow key={r._id} hover sx={{'&:hover':{bgcolor:'#FFF8F5'}}}>
<TableCell sx={{color:'#999',fontSize:11}}>{(page-1)*PS+i+1}</TableCell>
<TableCell><Box display="flex" alignItems="center" gap={1}>
<Avatar sx={{width:26,height:26,fontSize:11,bgcolor:'#E8622A15',color:'#E8622A',fontWeight:700}}>{((r.lead?.name || ((r.lead?.firstName||'') + ' ' + (r.lead?.lastName||'')).trim() || r.lead?.phone || 'Unknown') || ((r.lead?.firstName||'') + ' ' + (r.lead?.lastName||'')).trim() || r.lead?.phone || 'Unknown')?.charAt(0)}</Avatar>
<Typography variant="body2" fontWeight={500}>{((r.lead?.name || ((r.lead?.firstName||'') + ' ' + (r.lead?.lastName||'')).trim() || r.lead?.phone || 'Unknown') || ((r.lead?.firstName||'') + ' ' + (r.lead?.lastName||'')).trim() || r.lead?.phone || 'Unknown')||'Unknown'}</Typography>
</Box></TableCell>
<TableCell sx={{fontSize:13,fontFamily:'monospace'}}>{r.lead?.phone||'-'}</TableCell>
<TableCell sx={{fontSize:12}}>{r.scheduledAt?new Date(r.scheduledAt).toLocaleString():'-'}</TableCell>
<TableCell sx={{fontSize:12,maxWidth:150,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{r.reason||'-'}</TableCell>
<TableCell><Chip label={r.priority} size="small" sx={{bgcolor:PC[r.priority]+'18',color:PC[r.priority],fontSize:10,height:20}}/></TableCell>
<TableCell><Chip label={r.status} size="small" sx={{bgcolor:SC[r.status]+'18',color:SC[r.status],fontWeight:600,fontSize:10,height:20}}/></TableCell>
<TableCell><Box display="flex" gap={0.5}>
                  {r.status==='pending'&&<Tooltip title="Mark Complete"><Button size="small" variant="contained" onClick={()=>markDone(r)} sx={{bgcolor:'#4CAF50','&:hover':{bgcolor:'#388E3C'},fontSize:10,py:0.3,px:1,minWidth:0,borderRadius:1}}>Done</Button></Tooltip>}
<Tooltip title="Edit"><IconButton size="small" onClick={()=>openEdit(r)} sx={{color:'#2196F3'}}><Edit sx={{fontSize:15}}/></IconButton></Tooltip>
<Tooltip title="Delete"><IconButton size="small" onClick={()=>del(r._id)} sx={{color:'#F44336'}}><Delete sx={{fontSize:15}}/></IconButton></Tooltip>
</Box></TableCell>
</TableRow>
            ))}
</TableBody>
</Table>
</TableContainer>
 
      <Box display="flex" justifyContent="space-between" alignItems="center" mt={2}>
<Typography variant="body2" color="text.secondary">Showing {rows.length>0?(page-1)*PS+1:0}–{Math.min(page*PS,filtered.length)} of {filtered.length}</Typography>
<Box display="flex" gap={0.5}>
<Button size="small" variant="outlined" disabled={page===1} onClick={()=>setPage(p=>p-1)} startIcon={<PrevIcon/>} sx={{minWidth:80,borderColor:'#E8622A',color:'#E8622A','&:disabled':{borderColor:'#ddd',color:'#ddd'}}}>Prev</Button>
          {Array.from({length:Math.min(tp,5)},(_,i)=>i+1).map(p=>(
<Button key={p} size="small" onClick={()=>setPage(p)} sx={{minWidth:32,height:32,p:0,borderRadius:1,bgcolor:page===p?'#E8622A':'transparent',color:page===p?'white':'#666'}}>{p}</Button>
          ))}
<Button size="small" variant="outlined" disabled={page===tp} onClick={()=>setPage(p=>p+1)} endIcon={<NextIcon/>} sx={{minWidth:80,borderColor:'#E8622A',color:'#E8622A','&:disabled':{borderColor:'#ddd',color:'#ddd'}}}>Next</Button>
</Box>
</Box>
 
      <Dialog open={open} onClose={()=>setOpen(false)} maxWidth="sm" fullWidth PaperProps={{sx:{borderRadius:3}}}>
<DialogTitle fontWeight={700}>{editR?'Edit Recall':'Add Recall'}</DialogTitle>
<DialogContent>
          {fe&&<Alert severity="error" sx={{mb:2,mt:1}}>{fe}</Alert>}
<Box display="flex" flexDirection="column" gap={2} mt={1}>
<FormControl fullWidth size="small"><InputLabel>Lead *</InputLabel>
<Select value={form.lead} label="Lead *" onChange={e=>setForm({...form,lead:e.target.value})}>
                {leads.map(l=><MenuItem key={l._id} value={l._id}>{l.name} — {l.phone}</MenuItem>)}
</Select>
</FormControl>
<TextField fullWidth label="Scheduled Date & Time *" type="datetime-local" value={form.scheduledAt} onChange={e=>setForm({...form,scheduledAt:e.target.value})} size="small" InputLabelProps={{shrink:true}}/>
<Box display="flex" gap={2}>
<FormControl fullWidth size="small"><InputLabel>Priority</InputLabel>
<Select value={form.priority} label="Priority" onChange={e=>setForm({...form,priority:e.target.value})}>
                  {['low','medium','high'].map(p=><MenuItem key={p} value={p}>{p.charAt(0).toUpperCase()+p.slice(1)}</MenuItem>)}
</Select>
</FormControl>
              {editR&&<FormControl fullWidth size="small"><InputLabel>Status</InputLabel>
<Select value={form.status||'pending'} label="Status" onChange={e=>setForm({...form,status:e.target.value})}>
                  {['pending','completed','missed','rescheduled'].map(s=><MenuItem key={s} value={s}>{s.charAt(0).toUpperCase()+s.slice(1)}</MenuItem>)}
</Select>
</FormControl>}
</Box>
<TextField fullWidth label="Reason" value={form.reason} onChange={e=>setForm({...form,reason:e.target.value})} size="small"/>
<TextField fullWidth label="Notes" value={form.notes} onChange={e=>setForm({...form,notes:e.target.value})} size="small" multiline rows={2}/>
</Box>
</DialogContent>
<DialogActions sx={{p:2}}>
<Button onClick={()=>setOpen(false)}>Cancel</Button>
<Button variant="contained" onClick={save} disabled={saving} sx={{bgcolor:'#E8622A','&:hover':{bgcolor:'#C4501F'},borderRadius:2,px:3}}>
            {saving?<CircularProgress size={18} sx={{color:'white'}}/>:(editR?'Update':'Add')}
</Button>
</DialogActions>
</Dialog>
</Box>
  );
}